import {Component, Property} from '@wonderlandengine/api';

/**
 * SkySphereControllerV2 - Simplified and fixed version
 * Works with a mesh sphere that has negative scale (-20, -20, -20) set in editor
 * 
 * For smooth transitions, create TWO sphere objects:
 * 1. Main sphere with this component
 * 2. Secondary sphere (child or sibling) for crossfade
 */
export class SkySphereControllerV2 extends Component {
    static TypeName = 'sky-sphere-controller-v2';
    static Properties = {
        dayMaterial: Property.material(),
        nightMaterial: Property.material(),
        secondarySphere: Property.object(),  // Optional: second sphere for smooth crossfade
        transitionDuration: Property.float(2.0),
        transitionType: Property.enum(['instant', 'fade-black', 'crossfade'], 'fade-black'),
        debugMode: Property.bool(false)
    };
    
    init() {
        this.currentState = 'day';
        this.targetState = 'day';
        this.isTransitioning = false;
        this.transitionProgress = 0;
        
        // Store materials for smooth blending
        this.meshComponent = null;
        this.dayMaterialClone = null;  // Cloned materials for alpha animation
        this.nightMaterialClone = null;
        
        console.log('[SkySphereV2] Initialized');
    }
    
    start() {
        // Validate materials
        if (!this.dayMaterial || !this.nightMaterial) {
            console.error('[SkySphereV2] Both day and night materials must be assigned!');
            return;
        }
        
        // Get mesh component
        this.meshComponent = this.object.getComponent('mesh');
        if (!this.meshComponent) {
            console.error('[SkySphereV2] No mesh component found!');
            return;
        }
        
        // Clone materials for unique alpha control
        // We need separate clones for each sphere to control alpha independently!
        this.dayMaterialClone = this.dayMaterial.clone();
        this.nightMaterialClone = this.nightMaterial.clone();
        
        // For crossfade mode, we need separate material instances for each sphere
        this.dayMaterialClone2 = null;
        this.nightMaterialClone2 = null;
        if (this.secondarySphere && this.transitionType === 2) {
            this.dayMaterialClone2 = this.dayMaterial.clone();
            this.nightMaterialClone2 = this.nightMaterial.clone();
        }
        
        // IMPORTANT: Initialize diffuseColor to ensure alpha channel exists
        // Set white color with full opacity for primary materials
        this.dayMaterialClone.setDiffuseColor([1.0, 1.0, 1.0, 1.0]);
        this.nightMaterialClone.setDiffuseColor([1.0, 1.0, 1.0, 1.0]);
        
        // Initialize secondary materials if they exist
        if (this.dayMaterialClone2) {
            this.dayMaterialClone2.setDiffuseColor([1.0, 1.0, 1.0, 1.0]);
            this.nightMaterialClone2.setDiffuseColor([1.0, 1.0, 1.0, 1.0]);
        }
        
        // Set initial material (day with full opacity)
        this.meshComponent.material = this.dayMaterialClone;
        this.setMaterialAlpha(this.dayMaterialClone, 1.0);
        
        console.log('[SkySphereV2] Configuration:', {
            transitionType: this.transitionType,
            transitionDuration: this.transitionDuration,
            hasSecondary: !!this.secondarySphere,
            debugMode: this.debugMode
        });
        
        // Setup secondary sphere for crossfade if available
        this.secondaryMesh = null;
        if (this.secondarySphere && (this.transitionType === 2 || this.transitionType === 1)) { // crossfade or fade-black
            this.secondaryMesh = this.secondarySphere.getComponent('mesh');
            if (this.secondaryMesh) {
                // IMPORTANT: Make secondary sphere slightly smaller to ensure it's inside
                // This prevents z-fighting and ensures proper render order
                const secondaryScale = new Float32Array(3);
                this.secondarySphere.getScalingLocal(secondaryScale);
                // Make it 99% of the size to be just inside the primary sphere
                this.secondarySphere.setScalingLocal([
                    secondaryScale[0] * 0.99,
                    secondaryScale[1] * 0.99,
                    secondaryScale[2] * 0.99
                ]);
                
                // Use separate material clones for secondary sphere
                if (this.transitionType === 2 && this.dayMaterialClone2) {
                    // For crossfade, use secondary material clones
                    this.secondaryMesh.material = this.dayMaterialClone2;
                    // Start with secondary at FULL opacity (will be hidden by primary)
                    this.setMaterialAlpha(this.dayMaterialClone2, 1.0);
                } else {
                    // For fade-black, can share materials since we don't overlap
                    this.secondaryMesh.material = this.nightMaterialClone;
                    this.setMaterialAlpha(this.nightMaterialClone, 0.0);
                }
                console.log('[SkySphereV2] Secondary sphere setup for transitions (scaled to 99%)');
            } else {
                console.warn('[SkySphereV2] Secondary sphere has no mesh component, falling back to instant');
                this.transitionType = 0; // Set to instant
            }
        }
        
        // Store original scales (but we won't modify them for alpha transitions)
        this.originalScale = new Float32Array(3);
        this.object.getScalingLocal(this.originalScale);
        
        // Don't touch the scale - it's set correctly in editor (-20, -20, -20)
        console.log('[SkySphereV2] Using editor-set scale, transition type:', this.transitionType);
        
        // Setup controls
        if (this.debugMode) {
            this.setupDebugControls();
        }
        
        console.log('[SkySphereV2] Ready');
    }
    
    setupDebugControls() {
        console.log('[SkySphereV2] Debug Controls:');
        console.log('  N - Transition to night');
        console.log('  M - Transition to day');
        console.log('  + - Increase transition speed');
        console.log('  - - Decrease transition speed');
        console.log(`  Current duration: ${this.transitionDuration}s`);
        
        this.onKeyDown = (event) => {
            if (event.code === 'KeyN' && this.currentState !== 'night' && !this.isTransitioning) {
                this.startTransition('night');
            } else if (event.code === 'KeyM' && this.currentState !== 'day' && !this.isTransitioning) {
                this.startTransition('day');
            } else if (event.code === 'Equal' || event.code === 'NumpadAdd') {
                // Increase speed (decrease duration)
                this.transitionDuration = Math.max(0.5, this.transitionDuration - 0.5);
                console.log(`[SkySphereV2] Transition duration: ${this.transitionDuration}s`);
            } else if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
                // Decrease speed (increase duration)
                this.transitionDuration = Math.min(10, this.transitionDuration + 0.5);
                console.log(`[SkySphereV2] Transition duration: ${this.transitionDuration}s`);
            }
        };
        
        window.addEventListener('keydown', this.onKeyDown);
    }
    
    startTransition(target) {
        if (this.isTransitioning || this.currentState === target) return;
        
        // Convert enum index to string
        const transitionTypes = ['instant', 'fade-black', 'crossfade'];
        const transitionTypeString = transitionTypes[this.transitionType] || 'instant';
        
        console.log(`[SkySphereV2] Starting ${transitionTypeString} transition to ${target} (enum value: ${this.transitionType})`);
        
        // Check if materials are properly assigned
        if (!this.meshComponent) {
            console.error('[SkySphereV2] ERROR: No mesh component!');
            return;
        }
        
        const targetMat = target === 'night' ? this.nightMaterial : this.dayMaterial;
        if (!targetMat) {
            console.error(`[SkySphereV2] ERROR: ${target} material not assigned in editor!`);
            return;
        }
        
        this.targetState = target;
        this.transitionProgress = 0;
        
        // Handle different transition types using the index
        switch (this.transitionType) {
            case 2: // 'crossfade'
                if (this.secondaryMesh && this.secondarySphere) {
                    // Setup materials on spheres for crossfade using separate clones
                    if (target === 'night') {
                        // Day to night: primary has day (fade out), secondary has night (always visible)
                        this.meshComponent.material = this.dayMaterialClone;
                        this.secondaryMesh.material = this.nightMaterialClone2 || this.nightMaterialClone;
                        // Primary starts opaque, secondary is fully visible behind it
                        this.setMaterialAlpha(this.dayMaterialClone, 1.0);
                        // Secondary at full opacity - it's behind so will be revealed as primary fades
                        this.setMaterialAlpha(this.nightMaterialClone2 || this.nightMaterialClone, 1.0);
                    } else {
                        // Night to day: primary has night (fade out), secondary has day (always visible)
                        this.meshComponent.material = this.nightMaterialClone;
                        this.secondaryMesh.material = this.dayMaterialClone2 || this.dayMaterialClone;
                        // Primary starts opaque, secondary is fully visible behind it
                        this.setMaterialAlpha(this.nightMaterialClone, 1.0);
                        // Secondary at full opacity - it's behind so will be revealed as primary fades
                        this.setMaterialAlpha(this.dayMaterialClone2 || this.dayMaterialClone, 1.0);
                    }
                    this.isTransitioning = true;
                    // Performance monitoring
                    this.transitionStartTime = performance.now();
                    this.frameCount = 0;
                    this.lastFrameTime = performance.now();
                    console.log(`[SkySphereV2] Starting alpha crossfade transition to ${target}`);
                    console.log(`[SkySphereV2] Primary: ${this.currentState}, Secondary: ${target}`);
                } else {
                    // Fallback to instant
                    const mat = target === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                    this.meshComponent.material = mat;
                    this.setMaterialAlpha(mat, 1.0);
                    this.currentState = target;
                    this.isTransitioning = false;
                }
                break;
                
            case 1: // 'fade-black'
                // Start fade to black transition using alpha
                this.isTransitioning = true;
                this.fadeDirection = 'out';  // First fade out, then fade in
                console.log('[SkySphereV2] Starting fade-through-black transition (alpha-based)');
                break;
                
            case 0: // 'instant'
            default:
                // Immediate switch with cloned materials
                const instantMat = target === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                this.meshComponent.material = instantMat;
                this.setMaterialAlpha(instantMat, 1.0);
                this.currentState = target;
                this.isTransitioning = false;
                this.transitionProgress = 1.0;
                console.log('[SkySphereV2] Instant transition complete');
                break;
        }
    }
    
    update(dt) {
        if (!this.isTransitioning || !this.meshComponent) {
            return;
        }
        
        // Performance monitoring
        if (this.transitionStartTime) {
            this.frameCount++;
            const now = performance.now();
            const frameDelta = now - this.lastFrameTime;
            this.lastFrameTime = now;
            
            // Log performance issues
            if (frameDelta > 33) { // More than 33ms indicates < 30fps
                console.warn(`[SkySphereV2] Frame took ${frameDelta.toFixed(1)}ms (target: 16.67ms)`);
            }
            
            // Log every 10 frames in debug mode
            if (this.debugMode && this.frameCount % 10 === 0) {
                const elapsed = (now - this.transitionStartTime) / 1000;
                const avgFPS = this.frameCount / elapsed;
                console.log(`[SkySphereV2] Progress: ${Math.round(this.transitionProgress * 100)}%, FPS: ${avgFPS.toFixed(1)}, dt: ${dt.toFixed(3)}`);
            }
        }
        
        // Update transition progress
        this.transitionProgress += dt / this.transitionDuration;
        
        if (this.transitionProgress >= 1.0) {
            // Complete transition
            this.completeTransition();
            return;
        }
        
        // Calculate smooth transition value
        const t = this.smoothstep(0, 1, this.transitionProgress);
        
        // Handle different transition types (using numeric values)
        switch (this.transitionType) {
            case 2: // 'crossfade'
                // Alpha crossfade between two spheres
                if (this.secondaryMesh && this.secondarySphere) {
                    // New approach: Secondary is always at full opacity (behind primary)
                    // We only fade out the primary sphere to reveal secondary
                    
                    let primaryMat;
                    
                    if (this.targetState === 'night') {
                        // Day to night: fade out day material
                        primaryMat = this.dayMaterialClone;
                    } else {
                        // Night to day: fade out night material
                        primaryMat = this.nightMaterialClone;
                    }
                    
                    // Only fade out primary sphere (1 to 0)
                    // Secondary stays at 1.0 (set during startTransition)
                    this.setMaterialAlpha(primaryMat, 1.0 - t);
                    
                    if (this.debugMode && Math.floor(t * 10) !== Math.floor((t - dt / this.transitionDuration) * 10)) {
                        console.log(`[SkySphereV2] Alpha crossfade: ${Math.round(t * 100)}%`);
                    }
                }
                break;
                
            case 1: // 'fade-black'
                // Fade through black transition using alpha
                if (this.fadeDirection === 'out') {
                    // First half: fade out to black
                    const fadeOut = 1.0 - (t * 2);  // 1 to 0 in first half
                    
                    // Fade current material alpha to 0
                    const currentMat = this.currentState === 'day' ? this.dayMaterialClone : this.nightMaterialClone;
                    this.setMaterialAlpha(currentMat, fadeOut);
                    
                    // Switch material at midpoint
                    if (t >= 0.5) {
                        // Switch to target material at alpha 0
                        const targetMat = this.targetState === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                        this.meshComponent.material = targetMat;
                        this.fadeDirection = 'in';
                        console.log(`[SkySphereV2] Switched to ${this.targetState} material`);
                    }
                } else {
                    // Second half: fade in from black
                    const fadeIn = (t - 0.5) * 2;  // 0 to 1 in second half
                    
                    // Fade new material alpha from 0 to 1
                    const targetMat = this.targetState === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                    this.setMaterialAlpha(targetMat, fadeIn);
                }
                
                if (this.debugMode && Math.floor(t * 10) !== Math.floor((t - dt / this.transitionDuration) * 10)) {
                    console.log(`[SkySphereV2] Alpha fade-black: ${Math.round(t * 100)}%`);
                }
                break;
                
            default:
                // Should not reach here for instant transitions
                break;
        }
    }
    
    completeTransition() {
        this.isTransitioning = false;
        this.currentState = this.targetState;
        this.transitionProgress = 1.0;
        
        // Ensure final material is set
        const finalMaterial = this.getTargetMaterial();
        if (this.meshComponent.material !== finalMaterial) {
            this.meshComponent.material = finalMaterial;
        }
        this.currentMaterial = finalMaterial;
        
        // Reset alpha values based on transition type
        switch (this.transitionType) {
            case 2: // 'crossfade'
                // After crossfade, swap materials so primary has the new sky
                const targetMat = this.targetState === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                const targetMat2 = this.targetState === 'night' ? 
                    (this.nightMaterialClone2 || this.nightMaterialClone) : 
                    (this.dayMaterialClone2 || this.dayMaterialClone);
                
                // Set primary sphere to show target material at full opacity
                this.meshComponent.material = targetMat;
                this.setMaterialAlpha(targetMat, 1.0);
                
                // Keep secondary ready but behind (will be set up fresh next transition)
                if (this.secondaryMesh) {
                    // Secondary stays at full opacity but behind primary
                    this.setMaterialAlpha(targetMat2, 1.0);
                }
                break;
                
            case 1: // 'fade-black'
                // Ensure final material is at full opacity
                const finalMat = this.targetState === 'night' ? this.nightMaterialClone : this.dayMaterialClone;
                this.meshComponent.material = finalMat;
                this.setMaterialAlpha(finalMat, 1.0);
                break;
        }
        
        console.log(`[SkySphereV2] Transition complete: ${this.currentState}`);
    }
    
    getTargetMaterial() {
        return this.targetState === 'night' ? this.nightMaterial : this.dayMaterial;
    }
    
    smoothstep(edge0, edge1, x) {
        const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
        return t * t * (3 - 2 * t);
    }
    
    setMaterialAlpha(material, alpha) {
        // For Phong materials - use diffuseColor with alpha channel
        // The alpha works with Phong Opaque Textured materials!
        if (material.getDiffuseColor) {
            const color = material.getDiffuseColor();
            material.setDiffuseColor([color[0], color[1], color[2], alpha]);
            
            // ALSO set emissive color to help with blending
            // This might help if the issue is with how emissive textures blend
            if (material.setEmissiveColor) {
                // Set emissive intensity based on alpha for smoother blending
                material.setEmissiveColor([alpha, alpha, alpha, 1.0]);
            }
            
            // Log exact alpha values to detect quantization
            if (this.debugMode && material.diffuseTexture) {
                // Get back the actual set value to see if it's being quantized
                const newColor = material.getDiffuseColor();
                const actualAlpha = newColor[3];
                
                // Check if alpha is being quantized
                if (Math.abs(actualAlpha - alpha) > 0.01) {
                    console.warn(`[SkySphereV2] Alpha quantization detected! Requested: ${alpha.toFixed(4)}, Got: ${actualAlpha.toFixed(4)}`);
                }
                
                // Log at specific thresholds that might cause jumps
                const thresholds = [0.0, 0.25, 0.5, 0.75, 1.0];
                for (let threshold of thresholds) {
                    if (Math.abs(alpha - threshold) < 0.02) {
                        console.log(`[SkySphereV2] Near threshold ${threshold}: alpha = ${alpha.toFixed(4)}`);
                    }
                }
            }
        } else if (material.getFlatColor) {
            // Fallback for Flat materials (won't blend but included for completeness)
            const color = material.getFlatColor();
            material.setFlatColor([color[0], color[1], color[2], alpha]);
        }
    }
    
    onDestroy() {
        if (this.onKeyDown) {
            window.removeEventListener('keydown', this.onKeyDown);
        }
    }
    
    // Public API
    transitionToNight() {
        console.log('[SkySphereV2] transitionToNight() called');
        this.startTransition('night');
    }
    
    transitionToDay() {
        console.log('[SkySphereV2] transitionToDay() called');
        this.startTransition('day');
    }
    
    getCurrentState() {
        return this.currentState;
    }
    
    isInTransition() {
        return this.isTransitioning;
    }
}